import { useEffect, useState } from "react";
import axios from "axios";
import { cloneDeep, set as lodashSet } from "lodash";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings, LogOut, Save, Plus, Trash2, Eye, EyeOff, Loader2 } from "lucide-react";
import { useSettingsCtx } from "@/lib/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ntoa_admin_token";

const Field = ({ label, value, onChange, textarea, type = "text", testid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label>
    {textarea ? (
      <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} rows={3}
        className="bg-slate-50 border-slate-300 text-navy-dark" />
    ) : (
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="bg-slate-50 border-slate-300 text-navy-dark" />
    )}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2.5 cursor-pointer select-none">
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    <span className="text-sm text-zinc-300">{label}</span>
  </label>
);

const SectionCard = ({ children }) => (
  <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">{children}</div>
);

export const AdminPanel = () => {
  const { content, refresh } = useSettingsCtx();
  const [open, setOpen] = useState(() =>
    typeof window !== "undefined" && window.location.search.includes("openadmin")
  );
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [password, setPassword] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [uploading, setUploading] = useState(false);

  const uploadFlyer = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      update("promo.flyerUrl", data.url);
      toast.success("Flyer uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Verify token whenever the panel opens
  useEffect(() => {
    window.__adminOpen = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setDraft(cloneDeep(content));
    const t = localStorage.getItem(TOKEN_KEY) || "";
    if (!t) { setAuthed(false); return; }
    setChecking(true);
    axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => { setAuthed(true); setToken(t); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); })
      .finally(() => setChecking(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      setPassword("");
      setDraft(cloneDeep(content));
      toast.success("Welcome back, admin");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
  };

  const update = (path, value) => setDraft((prev) => { const n = cloneDeep(prev); lodashSet(n, path, value); return n; });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, { data: draft }, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      toast.success("Site updated — changes are live");
    } catch (e) {
      if (e.response?.status === 401) { logout(); toast.error("Session expired, please log in again"); }
      else toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: pw.current, new_password: pw.next },
        { headers: { Authorization: `Bearer ${token}` } });
      setPw({ current: "", next: "" });
      toast.success("Password changed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-amber-400 transition-colors"
          data-testid="admin-open-btn"
        >
          <Settings size={13} /> Admin
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-white border-slate-200 text-navy-dark max-h-[88vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="p-6 pb-3 border-b border-slate-200">
          <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <Settings size={18} className="text-amber-500" /> Site Settings
          </DialogTitle>
          <DialogDescription className="sr-only">Edit your site content, pricing, promotions and legal pages.</DialogDescription>
        </DialogHeader>

        {!authed ? (
          <div className="p-8 space-y-4 max-w-sm mx-auto w-full">
            <p className="text-sm text-zinc-400">Enter the admin password to edit the site.</p>
            <Input
              type="password" placeholder="Admin password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && login()}
              data-testid="admin-password-input"
              className="bg-slate-50 border-slate-300 text-navy-dark"
            />
            <Button onClick={login} disabled={checking || !password} className="w-full bg-amber-500 text-white hover:bg-amber-600" data-testid="admin-login-btn">
              {checking ? <Loader2 className="animate-spin" size={16} /> : "Log In"}
            </Button>
          </div>
        ) : !draft ? null : (
          <>
            <Tabs defaultValue="general" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="mx-6 mt-4 bg-slate-100 flex-wrap h-auto">
                <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
                <TabsTrigger value="benefits" data-testid="tab-benefits">Benefits</TabsTrigger>
                <TabsTrigger value="membership" data-testid="tab-membership">Membership</TabsTrigger>
                <TabsTrigger value="promo" data-testid="tab-promo">Promo</TabsTrigger>
                <TabsTrigger value="legal" data-testid="tab-legal">Legal</TabsTrigger>
                <TabsTrigger value="password" data-testid="tab-password">Password</TabsTrigger>
              </TabsList>

              <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
                {/* GENERAL */}
                <TabsContent value="general" className="space-y-5 mt-0">
                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Hero</h4>
                    <Field label="Overline" value={draft.hero.overline} onChange={(v) => update("hero.overline", v)} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Title line 1" value={draft.hero.titleLine1} onChange={(v) => update("hero.titleLine1", v)} />
                      <Field label="Title line 2" value={draft.hero.titleLine2} onChange={(v) => update("hero.titleLine2", v)} />
                    </div>
                    <Field label="Subtitle" textarea value={draft.hero.subtitle} onChange={(v) => update("hero.subtitle", v)} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Primary button" value={draft.hero.primaryCta} onChange={(v) => update("hero.primaryCta", v)} />
                      <Field label="Secondary button" value={draft.hero.secondaryCta} onChange={(v) => update("hero.secondaryCta", v)} />
                    </div>
                  </SectionCard>

                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Stats</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {draft.stats.map((s, i) => (
                        <div key={i} className="space-y-2">
                          <Field label={`Value ${i + 1}`} value={s.value} onChange={(v) => update(`stats.${i}.value`, v)} />
                          <Field label="Label" value={s.label} onChange={(v) => update(`stats.${i}.label`, v)} />
                        </div>
                      ))}
                    </div>
                  </SectionCard>

                  <SectionCard>
                    <div className="flex items-center justify-between">
                      <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Marquee ribbon</h4>
                      <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-amber-400"
                        onClick={() => update("marquee", [...draft.marquee, "New item"])}>
                        <Plus size={14} /> Add
                      </Button>
                    </div>
                    {draft.marquee.map((m, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input value={m} onChange={(e) => update(`marquee.${i}`, e.target.value)} className="bg-slate-50 border-slate-300 text-navy-dark" />
                        <button className="text-zinc-500 hover:text-red-400" onClick={() => update("marquee", draft.marquee.filter((_, j) => j !== i))}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </SectionCard>

                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">About</h4>
                    <Field label="Chapter label" value={draft.about.chapter} onChange={(v) => update("about.chapter", v)} />
                    <Field label="Heading" value={draft.about.heading} onChange={(v) => update("about.heading", v)} />
                    <Field label="Paragraph 1" textarea value={draft.about.paragraphs[0]} onChange={(v) => update("about.paragraphs.0", v)} />
                    <Field label="Paragraph 2" textarea value={draft.about.paragraphs[1]} onChange={(v) => update("about.paragraphs.1", v)} />
                    <Field label="Image badge" value={draft.about.badge} onChange={(v) => update("about.badge", v)} />
                  </SectionCard>

                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Contact / Footer</h4>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Heading line 1" value={draft.contact.headingLine1} onChange={(v) => update("contact.headingLine1", v)} />
                      <Field label="Heading line 2" value={draft.contact.headingLine2} onChange={(v) => update("contact.headingLine2", v)} />
                      <Field label="Email" value={draft.contact.email} onChange={(v) => update("contact.email", v)} />
                      <Field label="Phone" value={draft.contact.phone} onChange={(v) => update("contact.phone", v)} />
                      <Field label="Location" value={draft.contact.location} onChange={(v) => update("contact.location", v)} />
                      <Field label="Join button" value={draft.contact.joinCta} onChange={(v) => update("contact.joinCta", v)} />
                    </div>
                  </SectionCard>
                </TabsContent>

                {/* BENEFITS */}
                <TabsContent value="benefits" className="space-y-4 mt-0">
                  <Field label="Section heading" value={draft.benefits.heading} onChange={(v) => update("benefits.heading", v)} />
                  {draft.benefits.items.map((it, i) => (
                    <SectionCard key={i}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-widest text-zinc-500">{it.icon}</span>
                        <div className="flex gap-5">
                          <Toggle label="Coming soon" checked={it.comingSoon} onChange={(v) => update(`benefits.items.${i}.comingSoon`, v)} testid={`benefit-${i}-comingsoon`} />
                          <Toggle label="Hidden" checked={it.hidden} onChange={(v) => update(`benefits.items.${i}.hidden`, v)} testid={`benefit-${i}-hidden`} />
                        </div>
                      </div>
                      <Field label="Title" value={it.title} onChange={(v) => update(`benefits.items.${i}.title`, v)} />
                      <Field label="Description" textarea value={it.desc} onChange={(v) => update(`benefits.items.${i}.desc`, v)} />
                      {typeof it.stat !== "undefined" && (
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Stat" value={it.stat} onChange={(v) => update(`benefits.items.${i}.stat`, v)} />
                          <Field label="Stat unit" value={it.statUnit} onChange={(v) => update(`benefits.items.${i}.statUnit`, v)} />
                        </div>
                      )}
                    </SectionCard>
                  ))}
                </TabsContent>

                {/* MEMBERSHIP */}
                <TabsContent value="membership" className="space-y-4 mt-0">
                  <Field label="Section heading" value={draft.membership.heading} onChange={(v) => update("membership.heading", v)} />
                  <Field label="Subtitle" textarea value={draft.membership.subtitle} onChange={(v) => update("membership.subtitle", v)} />
                  {draft.membership.tiers.map((t, ti) => (
                    <SectionCard key={ti}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs uppercase tracking-widest text-amber-500 font-bold">Tier {ti + 1}</span>
                        <div className="flex gap-5">
                          <Toggle label="Highlight" checked={t.highlight} onChange={(v) => update(`membership.tiers.${ti}.highlight`, v)} />
                          <Toggle label="Hidden" checked={t.hidden} onChange={(v) => update(`membership.tiers.${ti}.hidden`, v)} testid={`tier-${ti}-hidden`} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Name" value={t.name} onChange={(v) => update(`membership.tiers.${ti}.name`, v)} />
                        <Field label="Subtitle" value={t.subtitle} onChange={(v) => update(`membership.tiers.${ti}.subtitle`, v)} />
                        <Field label="List price (strikethrough)" value={t.listPrice} onChange={(v) => update(`membership.tiers.${ti}.listPrice`, v)} testid={`tier-${ti}-listprice`} />
                        <Field label="Sale price" value={t.salePrice} onChange={(v) => update(`membership.tiers.${ti}.salePrice`, v)} testid={`tier-${ti}-price`} />
                        <Field label="Savings badge" value={t.savings} onChange={(v) => update(`membership.tiers.${ti}.savings`, v)} />
                        <Field label="Button text" value={t.cta} onChange={(v) => update(`membership.tiers.${ti}.cta`, v)} />
                      </div>
                      <Field label="Tagline / note" value={t.note} onChange={(v) => update(`membership.tiers.${ti}.note`, v)} />
                      <Field label="Button text" value={t.cta} onChange={(v) => update(`membership.tiers.${ti}.cta`, v)} />

                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs uppercase tracking-widest text-zinc-500">Features</span>
                        <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-amber-400"
                          onClick={() => update(`membership.tiers.${ti}.features`, [...t.features, { label: "New feature", comingSoon: false, hidden: false }])}>
                          <Plus size={14} /> Add
                        </Button>
                      </div>
                      {t.features.map((f, fi) => (
                        <div key={fi} className="rounded border border-slate-200 bg-slate-50 p-2.5 space-y-2">
                          <div className="flex gap-2 items-center">
                            <Input value={f.label} onChange={(e) => update(`membership.tiers.${ti}.features.${fi}.label`, e.target.value)}
                              className="bg-slate-50 border-slate-300 text-navy-dark" />
                            <button className="text-zinc-500 hover:text-red-400"
                              onClick={() => update(`membership.tiers.${ti}.features`, t.features.filter((_, j) => j !== fi))}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                          <div className="flex gap-5">
                            <Toggle label="Coming soon" checked={f.comingSoon} onChange={(v) => update(`membership.tiers.${ti}.features.${fi}.comingSoon`, v)} />
                            <Toggle label="Hidden" checked={f.hidden} onChange={(v) => update(`membership.tiers.${ti}.features.${fi}.hidden`, v)} />
                          </div>
                        </div>
                      ))}
                    </SectionCard>
                  ))}
                </TabsContent>

                {/* PROMO */}
                <TabsContent value="promo" className="space-y-4 mt-0">
                  <SectionCard>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Promotion / Giveaway</h4>
                      <div className="flex gap-5">
                        <Toggle label="Active" checked={draft.promo?.enabled} onChange={(v) => update("promo.enabled", v)} testid="promo-enabled" />
                        <Toggle label="Show popup" checked={draft.promo?.showPopup} onChange={(v) => update("promo.showPopup", v)} testid="promo-showpopup" />
                      </div>
                    </div>
                    <p className="text-xs text-zinc-400">Shows a top banner and (optional) flyer popup while active and within the date window. Leave dates blank to run indefinitely while Active is on.</p>
                    <Field label="Banner text" textarea value={draft.promo?.bannerText} onChange={(v) => update("promo.bannerText", v)} testid="promo-bannertext" />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Button label" value={draft.promo?.bannerCtaLabel} onChange={(v) => update("promo.bannerCtaLabel", v)} />
                      <Field label="Button link (URL)" value={draft.promo?.bannerCtaUrl} onChange={(v) => update("promo.bannerCtaUrl", v)} />
                      <Field label="Start (optional)" type="datetime-local" value={draft.promo?.startDate} onChange={(v) => update("promo.startDate", v)} testid="promo-start" />
                      <Field label="End / expires (optional)" type="datetime-local" value={draft.promo?.endDate} onChange={(v) => update("promo.endDate", v)} testid="promo-end" />
                    </div>
                  </SectionCard>

                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Flyer image</h4>
                    {draft.promo?.flyerUrl ? (
                      <img
                        src={draft.promo.flyerUrl.startsWith("http") ? draft.promo.flyerUrl : `${process.env.REACT_APP_BACKEND_URL}${draft.promo.flyerUrl}`}
                        alt="Flyer preview"
                        className="max-h-56 rounded-md border border-slate-200"
                        data-testid="promo-flyer-preview"
                      />
                    ) : (
                      <p className="text-xs text-zinc-400">No flyer uploaded yet.</p>
                    )}
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-amber-500 text-white font-semibold text-sm cursor-pointer hover:bg-amber-600 transition-colors" data-testid="promo-upload-label">
                        {uploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Upload flyer
                        <input type="file" accept="image/*" className="hidden" onChange={uploadFlyer} data-testid="promo-upload-input" />
                      </label>
                      {draft.promo?.flyerUrl && (
                        <button className="text-sm text-zinc-500 hover:text-red-500" onClick={() => update("promo.flyerUrl", "")}>Remove</button>
                      )}
                    </div>
                    <Field label="Flyer alt text" value={draft.promo?.flyerAlt} onChange={(v) => update("promo.flyerAlt", v)} />
                  </SectionCard>
                </TabsContent>

                {/* LEGAL */}
                <TabsContent value="legal" className="space-y-4 mt-0">
                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Purpose</h4>
                    <p className="text-xs text-zinc-400">Shown on the Bylaws page (footer → Bylaws).</p>
                    <Textarea value={draft.legal?.purpose ?? ""} onChange={(e) => update("legal.purpose", e.target.value)} rows={4} className="bg-slate-50 border-slate-300 text-navy-dark" data-testid="legal-purpose" />
                  </SectionCard>
                  <SectionCard>
                    <h4 className="text-amber-500 text-xs uppercase tracking-widest font-bold">Bylaws</h4>
                    <p className="text-xs text-zinc-400">Blank lines create paragraph breaks.</p>
                    <Textarea value={draft.legal?.bylaws ?? ""} onChange={(e) => update("legal.bylaws", e.target.value)} rows={14} className="bg-slate-50 border-slate-300 text-navy-dark font-mono text-xs" data-testid="legal-bylaws" />
                  </SectionCard>
                </TabsContent>

                {/* PASSWORD */}
                <TabsContent value="password" className="space-y-4 mt-0 max-w-sm">
                  <Field label="Current password" type="password" value={pw.current} onChange={(v) => setPw({ ...pw, current: v })} testid="pw-current" />
                  <Field label="New password" type="password" value={pw.next} onChange={(v) => setPw({ ...pw, next: v })} testid="pw-next" />
                  <Button onClick={changePassword} disabled={!pw.current || !pw.next} className="bg-amber-500 text-white hover:bg-amber-600" data-testid="pw-save">
                    Update Password
                  </Button>
                </TabsContent>
              </div>
            </Tabs>

            {/* Footer actions */}
            <div className="border-t border-slate-200 p-4 flex items-center justify-between">
              <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-red-400 transition-colors" data-testid="admin-logout-btn">
                <LogOut size={15} /> Log out
              </button>
              <Button onClick={save} disabled={saving} className="bg-amber-500 text-white hover:bg-amber-600 font-bold" data-testid="admin-save-btn">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-1.5" /> Save changes</>}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
